/**
 * Tiny stats shim used by the gateway AI analytics tools. Mirrors the
 * subset of simple-statistics the editor uses so we don't pull in the
 * full ~100KB dependency just to compute mean / Pearson / OLS.
 *
 * Every function below assumes numeric input that's already been
 * filtered for NaN — callers handle the cleanup before passing in.
 */

export function mean(xs) {
  if (!xs.length) return NaN;
  let s = 0;
  for (const x of xs) s += x;
  return s / xs.length;
}

export function variance(xs) {
  if (xs.length < 2) return 0;
  const mu = mean(xs);
  let s = 0;
  for (const x of xs) s += (x - mu) * (x - mu);
  return s / xs.length;
}

export function standardDeviation(xs) {
  return Math.sqrt(variance(xs));
}

export function median(xs) {
  if (!xs.length) return NaN;
  const sorted = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

export function min(xs) {
  let m = Infinity;
  for (const x of xs) if (x < m) m = x;
  return m === Infinity ? NaN : m;
}

export function max(xs) {
  let m = -Infinity;
  for (const x of xs) if (x > m) m = x;
  return m === -Infinity ? NaN : m;
}

// quantile uses linear interpolation between adjacent ranks — same as
// simple-statistics's default and consistent with numpy's "linear"
// quantile method. p in [0, 1].
export function quantile(xs, p) {
  if (!xs.length) return NaN;
  const sorted = [...xs].sort((a, b) => a - b);
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

// zScore returns (x - mean) / sd. Caller is responsible for passing a
// non-zero sd; we return Infinity for sd === 0 rather than NaN so
// downstream "is this > threshold" checks behave predictably.
export function zScore(x, mu, sd) {
  if (sd === 0) return x === mu ? 0 : (x > mu ? Infinity : -Infinity);
  return (x - mu) / sd;
}

// OLS linear regression. Returns {m, b} where y = m*x + b.
export function linearRegression(pairs) {
  if (!Array.isArray(pairs) || pairs.length < 2) return { m: 0, b: 0 };
  let sx = 0, sy = 0, sxx = 0, sxy = 0;
  for (const [x, y] of pairs) {
    sx += x;
    sy += y;
    sxx += x * x;
    sxy += x * y;
  }
  const n = pairs.length;
  const denom = n * sxx - sx * sx;
  if (denom === 0) return { m: 0, b: sy / n };
  const m = (n * sxy - sx * sy) / denom;
  const b = (sy - m * sx) / n;
  return { m, b };
}

export function linearRegressionLine({ m, b }) {
  return (x) => m * x + b;
}

// rSquared on a residual basis: 1 - SSres/SStot. Returns 0 when SStot
// is zero (constant series) — a flat line "explains" all of itself.
export function rSquared(pairs, line) {
  if (!Array.isArray(pairs) || pairs.length < 2) return 0;
  let yMean = 0;
  for (const [, y] of pairs) yMean += y;
  yMean /= pairs.length;
  let ssRes = 0;
  let ssTot = 0;
  for (const [x, y] of pairs) {
    const yhat = line(x);
    ssRes += (y - yhat) * (y - yhat);
    ssTot += (y - yMean) * (y - yMean);
  }
  if (ssTot === 0) return 0;
  return 1 - ssRes / ssTot;
}

// Pearson sample correlation. NaN when either series has zero variance —
// caller checks before calling.
export function sampleCorrelation(xs, ys) {
  if (xs.length !== ys.length || xs.length < 2) return NaN;
  const mx = mean(xs);
  const my = mean(ys);
  let sxy = 0;
  let sxx = 0;
  let syy = 0;
  for (let i = 0; i < xs.length; i += 1) {
    const dx = xs[i] - mx;
    const dy = ys[i] - my;
    sxy += dx * dy;
    sxx += dx * dx;
    syy += dy * dy;
  }
  if (sxx === 0 || syy === 0) return NaN;
  return sxy / Math.sqrt(sxx * syy);
}
